import React, { FC } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { TextField, StatusField, JsonField } from '~/components';
import { Longdash, time } from '~/utils';
import TerminateApplication from './TerminateApplication';
import { ApplicationRecord } from '~/types/applications';
import FullscreenDrawer from '~/components/FullscreenDrawer';
import ApiLocalizedTextField from '../ApiLocalizedTextField';
import {
  ApplicationStagesList,
  getTranslationPrefix,
} from '~/components/Applications/components/ApplicationStagesList';
import CopyableUUID from '~/components/CopyableUUID';

interface ApplicationShowProps {
  showData: ApplicationRecord | null;
  onClose: () => void;
  target: 'individuals' | 'businesses';
  targetId: string;
}

const ApplicationShow: FC<ApplicationShowProps> = (props) => {
  const { showData, onClose, target, targetId } = props;

  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(`components.Applications.${key}`);

  const classes = useStyles();

  return (
    <FullscreenDrawer
      open={!!showData}
      onClose={onClose}
      heading={t('drawer.heading')}
      subtitle={<CopyableUUID id={showData?.id} />}
    >
      {showData && (
        <Grid container spacing={2} className={classes.container}>
          <Grid item xs={12}>
            <ApiLocalizedTextField
              keyPrefix={getTranslationPrefix(showData)}
              secondKey='name'
              label={t('columns.processName')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('columns.createdAt')}>
              {showData.created ? time(showData.created).format('lll') : Longdash}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('columns.updatedAt')}>
              {showData.updated ? time(showData.updated).format('lll') : Longdash}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('columns.currentStages')}>
              <ApplicationStagesList application={showData} />
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('columns.status')}>
              <StatusField status={showData.status} />
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('drawer.stagesData')}>
              <JsonField
                json={showData.stagesData}
                reactJsonOptions={{
                  name: null,
                  collapsed: true,
                  enableClipboard: false,
                  displayDataTypes: false,
                  style: { wordBreak: 'break-all' },
                }}
              />
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('drawer.internalData')}>
              <JsonField
                json={showData.internalData}
                reactJsonOptions={{
                  name: null,
                  collapsed: true,
                  enableClipboard: false,
                  displayDataTypes: false,
                  style: { wordBreak: 'break-all' },
                }}
              />
            </TextField>
          </Grid>
          {showData?.status !== 'TERMINATED' && permissions?.includes('application.terminate') && (
            <Grid item xs={12}>
              <div className={classes.actionsRow}>
                <TerminateApplication
                  id={showData.id}
                  target={target}
                  targetId={targetId}
                  onClose={onClose}
                />
              </div>
            </Grid>
          )}
        </Grid>
      )}
    </FullscreenDrawer>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    paddingLeft: theme.spacing(4),
  },
  bottomSpacing: {
    marginBottom: theme.spacing(2),
  },
  jsonBlock: {
    background: '#0b1023',
    color: '#cde3ff',
    padding: theme.spacing(1),
    borderRadius: 4,
    overflow: 'auto',
    maxHeight: '40vh',
  },
  drawerContent: {
    paddingRight: theme.spacing(2),
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

export default ApplicationShow;
